import React, { useEffect, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '@actual-app/components/button';
import { SvgExpandArrow } from '@actual-app/components/icons/v0';
import { InitialFocus } from '@actual-app/components/initial-focus';
import { Input } from '@actual-app/components/input';
import { Text } from '@actual-app/components/text';
import { theme } from '@actual-app/components/theme';
import { View } from '@actual-app/components/view';
import { listen } from '@actual-app/core/platform/client/connection';
import { isElectron } from '@actual-app/core/shared/environment';
import * as Platform from '@actual-app/core/shared/platform';
import { tsToRelativeTime } from '@actual-app/core/shared/util';

import { closeBudget } from '#budgetfiles/budgetfilesSlice';
import { useContextMenu } from '#hooks/useContextMenu';
import { useLocale } from '#hooks/useLocale';
import { useMetadataPref } from '#hooks/useMetadataPref';
import { useNavigate } from '#hooks/useNavigate';
import { useSyncServerStatus } from '#hooks/useSyncServerStatus';
import { pushModal } from '#modals/modalsSlice';
import { useDispatch } from '#redux';
import { useIsNocturneTheme } from '#style/theme';

type BudgetNameProps = {
  children?: ReactNode;
};

export function BudgetName({ children }: BudgetNameProps) {
  const hasWindowButtons = !Platform.isBrowser && Platform.OS === 'mac';
  const isNocturne = useIsNocturneTheme();

  return (
    <View
      style={{
        flexDirection: 'row',
        alignItems: 'center',
        userSelect: 'none',
        transition: 'padding .4s',
        ...(isNocturne
          ? {
              minHeight: 42,
              paddingTop: hasWindowButtons ? 20 : 38,
              margin: '0 8.4px 22.4px 16.8px',
            }
          : {
              paddingTop: hasWindowButtons ? 20 : 35,
              height: 30,
              margin: '0 8px 23px 20px',
              ...(hasWindowButtons
                ? {
                    justifyContent: 'flex-start',
                  }
                : {}),
            }),
      }}
    >
      <View style={{ minWidth: 0, flexShrink: 1 }}>
        <EditableBudgetName isNocturne={isNocturne} />
        {isNocturne && <BudgetSyncStatus />}
      </View>

      <View style={{ flex: 1, flexDirection: 'row' }} />

      {children}
    </View>
  );
}

function BudgetSyncStatus() {
  const { t } = useTranslation();
  const locale = useLocale();
  const serverStatus = useSyncServerStatus();
  const [cloudFileId] = useMetadataPref('cloudFileId');
  const [syncState, setSyncState] = useState<
    'idle' | 'syncing' | 'error' | 'disabled'
  >('idle');
  const [lastSyncedAt, setLastSyncedAt] = useState<string | null>(null);
  const [, setClock] = useState(0);
  const cloudSyncInFlight = useRef(false);

  useEffect(() => {
    return listen('sync-event', event => {
      // Full budget syncs emit `start`, while bank sync and schedule posting
      // also emit unpaired `success` events on this shared channel.
      if (event.type === 'start') {
        cloudSyncInFlight.current = true;
        setSyncState('syncing');
      } else if (event.type === 'error' || event.type === 'unauthorized') {
        if (!cloudSyncInFlight.current) {
          return;
        }
        cloudSyncInFlight.current = false;
        setSyncState('error');
      } else if (event.type === 'success') {
        if (!cloudSyncInFlight.current) {
          return;
        }
        cloudSyncInFlight.current = false;
        setSyncState(event.syncDisabled ? 'disabled' : 'idle');
        if (cloudFileId && !event.syncDisabled) {
          setLastSyncedAt(String(Date.now()));
        }
      }
    });
  }, [cloudFileId]);

  useEffect(() => {
    if (!lastSyncedAt) {
      return;
    }

    const interval = window.setInterval(() => {
      setClock(clock => clock + 1);
    }, 60_000);

    return () => window.clearInterval(interval);
  }, [lastSyncedAt]);

  let label = t('Ready to sync');
  if (!cloudFileId || serverStatus === 'no-server') {
    label = t('Local budget');
  } else if (serverStatus === 'offline') {
    label = t('Offline');
  } else if (syncState === 'syncing') {
    label = t('Syncing…');
  } else if (syncState === 'error') {
    label = t('Sync needs attention');
  } else if (syncState === 'disabled') {
    label = t('Sync disabled');
  } else if (lastSyncedAt) {
    const elapsed = Date.now() - Number(lastSyncedAt);
    label =
      elapsed < 60_000
        ? t('Synced just now')
        : t('Synced {{relativeTime}}', {
            relativeTime: tsToRelativeTime(lastSyncedAt, locale),
          });
  }

  return (
    <Text
      style={{
        marginTop: 1,
        color: theme.sidebarBudgetName,
        fontSize: 11,
        lineHeight: '14px',
        whiteSpace: 'nowrap',
      }}
    >
      {label}
    </Text>
  );
}

function EditableBudgetName({ isNocturne }: { isNocturne: boolean }) {
  const { t } = useTranslation();
  const [budgetName, setBudgetNamePref] = useMetadataPref('budgetName');
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [editing, setEditing] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const { handleContextMenu } = useContextMenu({
    triggerRef,
    items: [
      {
        name: 'rename',
        text: t('Rename budget'),
        onClick: () => setEditing(true),
      },
      {
        name: 'settings',
        text: t('Settings'),
        onClick: () => void navigate('/settings'),
      },
      isElectron() && {
        name: 'loadBackup',
        text: t('Load Backup…'),
        onClick: () =>
          dispatch(pushModal({ modal: { name: 'load-backup', options: {} } })),
      },
      {
        name: 'close',
        text: t('Switch file'),
        onClick: () => void dispatch(closeBudget()),
      },
    ],
  });

  if (editing) {
    return (
      <InitialFocus>
        <Input
          style={{
            maxWidth: 'calc(100% - 23px)',
            fontSize: isNocturne ? 15 : 16,
            fontWeight: 500,
          }}
          defaultValue={budgetName}
          onEnter={newBudgetName => {
            if (newBudgetName.trim() !== '') {
              setBudgetNamePref(newBudgetName);
              setEditing(false);
            }
          }}
          onBlur={() => setEditing(false)}
        />
      </InitialFocus>
    );
  }

  return (
    <Button
      ref={triggerRef}
      data-testid="budget-name"
      variant="bare"
      style={{
        color: isNocturne ? theme.pageText : theme.sidebarBudgetName,
        fontSize: isNocturne ? 15 : 16,
        fontWeight: 500,
        marginLeft: -5,
        flex: '0 auto',
      }}
      onClick={handleContextMenu}
    >
      <Text style={{ whiteSpace: 'nowrap', overflow: 'hidden' }}>
        {budgetName || t('Unnamed')}
      </Text>
      <SvgExpandArrow
        width={7}
        height={7}
        style={{ flexShrink: 0, marginLeft: 5 }}
      />
    </Button>
  );
}
